







































































































































import {
  defineComponent,
  PropType,
  computed,
  ref,
  onMounted,
  watch,
} from "@nuxtjs/composition-api";
import { CartItemInterface } from "~/modules/GraphQL/types";
import EditItemCartAvaiableOptions from "./EditItemCartAvaiableOptions.vue";
import { getConfigurableOptionsByProduct } from "~/modules/catalog/product/helpers";
import useProductStock from "~/modules/catalog/product/composables/useProductStock";
import type { SelectedOption } from "~/modules/catalog/types";

import {
  getVariantName,
  getColorLabel,
  getSizeLabel,
} from "~/modules/checkout/helpers";
import { getFirstImageByVariants } from "~/modules/catalog/category/helpers";
import { getPrices } from "~/modules/catalog/product/getters/productGetters";

export default defineComponent({
  name: "EditItemCart",
  components: { EditItemCartAvaiableOptions },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object as PropType<CartItemInterface>,
      default: null,
    },
  },
  emits: ["close", "confirm", "update:visible"],
  setup(props, { emit }) {
    const { getProductListStock } = useProductStock();
    const parentSku = computed(() => {
      return props.product?.product?.sku.split("-")[0];
    });

    const configurableOptions = computed(
      () => props.product?.productParent?.configurable_options_map ?? []
    );

    const showColorAlert = ref(false);
    const showSizeAlert = ref(false);
    const availableOptions = computed(() => {
      return getConfigurableOptionsByProduct(configurableOptions.value);
    });

    const variants = computed(() => {
      return props.product?.productParent?.configurable_children ?? [];
    });

    const skuList = computed(() => {
      return variants.value.map((variant) => variant?.sku);
    });

    const checkCart = ref(false);
    const dataCheckOptionInstock = ref([]);
    const totalCount = ref(0);
    const selectedOption = ref({
      size: null,
      color: null,
    });

    const updateSelectedOption = (attributeOption: any) => {
      if (attributeOption.type === "color") {
        selectedOption.value.color = attributeOption;
      } else if (attributeOption.type === "size") {
        selectedOption.value.size = attributeOption;
      }
    };

    const checkStockBySkus = async () => {
      const paramSkus = skuList.value
        .map((sku) => encodeURIComponent(sku))
        .join(",");
      dataCheckOptionInstock.value = await getProductListStock(paramSkus);
      checkoption();
    };

    const findMatchingVariant = (selectedOption: SelectedOption) => {
      const selectedColor = selectedOption.color;
      const selectedSize = selectedOption.size;
      const configurableOpt = props.product?.productParent?.configurable_options_map || [];
      const hasColorAttribute = configurableOpt.some(
        (opt) => opt.attribute_code === "color"
      );
      const hasSizeAttribute = configurableOpt.some(
        (opt) => opt.attribute_code === "size"
      );

      return (
        variants.value.find((variant) => {

          const colorMatch =
            !hasColorAttribute ||
            !selectedColor ||
            variant.color?.toString() === selectedColor?.value_index?.toString();

          const sizeMatch =
            !hasSizeAttribute ||
            !selectedSize ||
            variant.size?.toString() === selectedSize?.value_index?.toString();

          if (hasColorAttribute && hasSizeAttribute) {
            return colorMatch && sizeMatch;
          }
          if (hasColorAttribute && !hasSizeAttribute) {
            return colorMatch;
          }
          if (!hasColorAttribute && hasSizeAttribute) {
            return colorMatch;
          }
        }) || null
      );
    };

    const findMatchingSku = (selectedOption: SelectedOption) => {
      return findMatchingVariant(selectedOption)?.sku || null
    };

    const productShowPrice = computed(() => {
      return findMatchingVariant(selectedOption.value) || props.product?.product;
    });

    const checkoption = () => {
      if (
        !dataCheckOptionInstock.value ||
        dataCheckOptionInstock.value.length < 1
      ) {
        checkCart.value = false;
        return;
      }

      let sku;
      const { color, size } = selectedOption.value;
      if (!color && !size) {
        sku = parentSku.value;
      } else if (color && !size) {
        sku = `${parentSku.value}-${color.label}`;
      } else if (!color && size) {
        sku = `${parentSku.value}-00000-${size.label}`;
      } // Todo: Tối ưu đoạn này
      else if (color && size) {
        sku = findMatchingSku(selectedOption.value);
      }
      if (sku) {
        checkSkuInStock(sku);
      }
    };

    const checkSkuInStock = (sku: string) => {
      if (dataCheckOptionInstock.value[sku]) {
        checkCart.value = dataCheckOptionInstock.value[sku]?.is_in_stock;
        totalCount.value = dataCheckOptionInstock.value[sku]?.qty;
      } else {
        totalCount.value = 0;
        for (const key in dataCheckOptionInstock.value) {
          if (dataCheckOptionInstock.value[key].sku.includes(sku)) {
            totalCount.value =
              totalCount.value + dataCheckOptionInstock.value[key]?.qty;
          }
        }

        if (totalCount.value > 0) {
          checkCart.value = true;
        } else {
          checkCart.value = false;
        }
      }
    };

    const getProductImages = computed(() => {
      if (selectedOption.value.color) {
        return getFirstImageByVariants(
          selectedOption.value.color,
          variants.value
        );
      }

      return props.product?.product?.thumbnail.url || "";
    });

    onMounted(async () => {
      const selectedColorLabel = getColorLabel(
        variants.value,
        props.product?.product?.sku,
        props.product?.productParent?.configurable_options_map
      );
      const selectedSizeLabel = getSizeLabel(
        variants.value,
        props.product?.product?.sku,
        props.product?.productParent?.configurable_options_map
      );
      selectedOption.value.color = availableOptions.value.color.find(
        (color) => color.label === selectedColorLabel
      );
      selectedOption.value.size = availableOptions.value.size.find(
        (size) => size.label === selectedSizeLabel
      );

      await checkStockBySkus();
    });

    watch(
      () => selectedOption.value,
      () => {
        checkoption();
      },
      { deep: true }
    );

    watch(
      () => selectedOption.value.color,
      (newVal, oldVal) => {
        if (!oldVal || newVal.uid === oldVal.uid) {
          return;
        }
        selectedOption.value.size = null;
      }
    );

    const closeModal = () => {
      emit("close");
      emit("update:visible", false);
    };

    const onConfirm = () => {
      emit(
        "confirm",
        findMatchingSku(selectedOption.value),
        props.product.uid,
        props.product.quantity
      );
      emit("update:visible", false);
    };

    return {
      closeModal,
      onConfirm,
      parentSku,
      getVariantName,
      getColorLabel,
      getSizeLabel,
      getPrices,
      availableOptions,
      selectedOption,
      updateSelectedOption,
      checkStockBySkus,
      checkCart,
      checkoption,
      showColorAlert,
      showSizeAlert,
      dataCheckOptionInstock,
      configurableOptions,
      getProductImages,
      variants,
      findMatchingSku,
      productShowPrice,
    };
  },
});
